/**
 * O registry PARCIAL do módulo Clientes — spec-009, [T011] / US-3 e US-8.
 *
 * O resumo operacional que a tela de Clientes já mostra (tarefas pendentes e
 * atrasadas, oportunidades abertas, pipeline e o último atendimento) passa a
 * ser desenhável DENTRO da conversa, sem que o `chat-message.vue` saiba que
 * este módulo existe: ele resolve a chave que o backend mandou
 * (`ui.component`) no `UI_REGISTRY` e monta. Cartão novo do módulo é uma
 * entrada aqui — nunca uma linha na bolha.
 *
 * ## ★ Este parcial é SEPARADO do parcial da carteira — por módulo dono
 *
 * `cliente/resumo` e as cinco chaves de `carteira/*` (T010) nascem na mesma
 * spec e contam a mesma história para o consultor, mas moram em módulos
 * diferentes: o resumo é da tela de Clientes, a carteira é do Consolidador.
 * Juntá-los num parcial só faria `clients` importar de `consolidator` (ou o
 * contrário) para desenhar cartão — uma dependência entre módulos criada por
 * conveniência de arquivo, que nenhuma leitura do repo explicaria depois.
 *
 * A regra que fica: **o parcial pertence ao módulo dono da TELA que o dado
 * abre**, e não à conversa que o pediu.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | helpers de unidade | `ia/adaptadores.js` | moeda, data e contagem, compartilhados |
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é snake_case (`valor_pipeline_brl`, `ultimo_atendimento_motivo`) e o
 * cartão fala o vocabulário da tela. Apontar direto deixaria o cartão montado
 * e VAZIO, que é a falha calada que este contrato existe para evitar.
 *
 * ## Duas decisões que valem explicar
 *
 * 1. **A flag é a do MÓDULO (`FEATURES.CLIENTS`).** É ela que gateia a rota
 *    `clientes` (`constants/navigation-routes.js:259`), que é a tela onde esses
 *    mesmos números aparecem. O registry é fail-closed na flag: escritório sem
 *    o módulo vê `fallback-card` com `flag_desligada`, em vez de um dado que
 *    ele não contratou.
 * 2. **`states` são os dois de leitura.** `input-available` entra porque é o
 *    estado em que a parte vive enquanto a tool roda: recusá-lo faria aparecer
 *    um cartão de ERRO (`estado_nao_suportado`) no meio de todo turno normal.
 *    Os desfechos de erro ficam de fora — este cartão não sabe desenhar uma
 *    falha, e o `fallback-card` diz o que aconteceu.
 *
 * ## O `label` é o do BACKEND
 *
 * `label` é o que o `tool-call-badge` mostra ENQUANTO a tool roda, e por isso é
 * byte a byte o `LABEL` de `get_cliente_resumo`. O `registry.unit.js` compara
 * com o `.py`: rótulo divergente não quebra nada visivelmente — o badge diz uma
 * coisa enquanto a tool roda e o thread diz outra quando o cartão chega.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

/** A chave do módulo no catálogo de navegação. */
const MODULO = ROUTE_KEYS.CLIENTES

/** A flag do módulo — ver a decisão 1 no cabeçalho. */
const FLAG_DO_MODULO = FEATURES.CLIENTS

// Enquanto a tool roda, e quando o resultado chega.
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<familia>/<cartao>` do contrato.
 *
 * Exportadas porque quem lê o registry e quem escreve o teste precisam da MESMA
 * string: uma chave redigitada não quebra nada visivelmente — o cartão
 * simplesmente não aparece.
 */
export const CHAVES = {
  RESUMO: 'cliente/resumo',
  SEGMENTACAO: 'cliente/segmentacao-carteira',
}

export default {
  [CHAVES.RESUMO]: {
    component: () => import('@modules/clients/ia/cartoes/resumo'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-cliente-resumo.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando cliente...',
  },
  // A carteira do consultor lida contra a régua de custódia do escritório
  // (spec-005, [T024]). Mora AQUI, e não em `consolidator`, pela mesma regra do
  // cabeçalho: o parcial pertence ao módulo dono da TELA que o dado abre — e o
  // dado é a carteira de CLIENTES. O botão de repasse aponta para a tela de
  // acessos do módulo `professionals`, mas destino não é dono.
  [CHAVES.SEGMENTACAO]: {
    component: () =>
      import('@modules/clients/ia/cartoes/segmentacao-carteira'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-cliente-segmentacao-carteira.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Lendo a sua carteira e aplicando a régua...',
  },
}
